import { ApiResponse, BfiApi } from '@apply/api/src/api/bfiApi';
import { logApplicationInsightsError } from '../../common/functions/applicationInsightFunctions';
import {
    BorrowerEntity,
    BusinessProfile,
    BusinessReference,
    KeyEmployee,
    Officer,
    OrgChartNode,
    Owner,
    WizardState,
} from '../types';
import { ApiErrorKind, BusinessProfileApiError } from './errors';
import { IBusinessProfileApi } from './IBusinessProfileApi';

const ROOT = '/businessprofile';

const KIND_BY_STATUS: Readonly<Record<number, ApiErrorKind>> = {
    400: ApiErrorKind.Validation,
    401: ApiErrorKind.Unauthorized,
    403: ApiErrorKind.Forbidden,
    404: ApiErrorKind.NotFound,
    409: ApiErrorKind.Conflict,
};

const kindFor = (status: number | null): ApiErrorKind => {
    if (status === null) {
        return ApiErrorKind.Network;
    }
    if (KIND_BY_STATUS[status]) {
        return KIND_BY_STATUS[status];
    }
    return status >= 500 ? ApiErrorKind.Server : ApiErrorKind.Unknown;
};

function unwrap<T>(response: ApiResponse<T> | null): T {
    const status = response?.statusCode ?? null;

    if (status === 200) {
        return response!.result;
    }

    const messages = (response?.errors ?? []).filter((e): e is string => typeof e === 'string');

    throw new BusinessProfileApiError(kindFor(status), {
        status,
        messages: messages.length > 0 ? messages : undefined,
    });
}

const query = (params: Record<string, string | number | undefined>): string => {
    const pairs = Object.entries(params)
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`);

    return pairs.length > 0 ? `?${pairs.join('&')}` : '';
};

// TODO: Helper for unimplemented methods, remove when all screens are released.
const notInThisRelease = <T,>(method: string): Promise<T> =>
    Promise.reject(
        new BusinessProfileApiError(ApiErrorKind.NotFound, {
            messages: [`${method} has no server endpoint in this release.`],
        }),
    );

export class BusinessProfileApi extends BfiApi implements IBusinessProfileApi {
    // `BaseApi` pops a global snackbar for every failure. The wizard reports failures against the
    // step they happened on, so showing both says the same thing twice and the generic one wins the
    // user's attention. Keep the Application Insights record, drop the snackbar.
    handleError = (error: unknown): void => {
        logApplicationInsightsError(error instanceof Error ? error : new Error(String(error)));
    };

    // ─── Wizard state ────────────────────────────────────────────────────────

    async getWizardState(): Promise<WizardState> {
        const state = unwrap(await super.get<ApiResponse<WizardState>>(`${ROOT}/wizard-state`));
        // The server leaves this null until the first step is completed.
        return { ...state, completedStepIds: state.completedStepIds ?? [] };
    }

    async saveWizardState(state: Partial<WizardState>): Promise<WizardState> {
        const saved = unwrap(await super.put<ApiResponse<WizardState>>(`${ROOT}/wizard-state`, state));
        return { ...saved, completedStepIds: saved.completedStepIds ?? [] };
    }

    // ─── Step 1: Business Profile ────────────────────────────────────────────

    // Null rather than a 404 when the user has not started: the step renders an empty form.
    async getBusinessProfile(): Promise<BusinessProfile | null> {
        return unwrap(await super.get<ApiResponse<BusinessProfile | null>>(`${ROOT}/profile`));
    }

    async saveBusinessProfile(profile: BusinessProfile): Promise<BusinessProfile> {
        return unwrap(await super.put<ApiResponse<BusinessProfile>>(`${ROOT}/profile`, profile));
    }

    // ─── Step 2: Borrower Entities ───────────────────────────────────────────

    // The endpoint scopes to the caller, so `businessProfileId` is not sent.
    async getBorrowerEntities(): Promise<BorrowerEntity[]> {
        return unwrap(await super.get<ApiResponse<BorrowerEntity[]>>(`${ROOT}/entities`));
    }

    async getBorrowerEntity(entityId: number): Promise<BorrowerEntity> {
        return unwrap(
            await super.get<ApiResponse<BorrowerEntity>>(`${ROOT}/entity${query({ entityId })}`),
        );
    }

    async saveBorrowerEntity(entity: BorrowerEntity): Promise<BorrowerEntity> {
        return unwrap(await super.put<ApiResponse<BorrowerEntity>>(`${ROOT}/entity`, entity));
    }

    async complete(): Promise<void> {
        unwrap(await super.post<ApiResponse<null>>(`${ROOT}/complete`));
    }

    // ─── TODO: Steps 3-7: not released ─────────────────────────────────────────────

    getOwners(): Promise<Owner[]> {
        return notInThisRelease('getOwners');
    }

    saveOwner(): Promise<Owner> {
        return notInThisRelease('saveOwner');
    }

    deleteOwner(): Promise<void> {
        return notInThisRelease('deleteOwner');
    }

    saveOwnerPercentages(): Promise<Owner[]> {
        return notInThisRelease('saveOwnerPercentages');
    }

    getOfficers(): Promise<Officer[]> {
        return notInThisRelease('getOfficers');
    }

    saveOfficer(): Promise<Officer> {
        return notInThisRelease('saveOfficer');
    }

    deleteOfficer(): Promise<void> {
        return notInThisRelease('deleteOfficer');
    }

    getKeyEmployees(): Promise<KeyEmployee[]> {
        return notInThisRelease('getKeyEmployees');
    }

    saveKeyEmployee(): Promise<KeyEmployee> {
        return notInThisRelease('saveKeyEmployee');
    }

    deleteKeyEmployee(): Promise<void> {
        return notInThisRelease('deleteKeyEmployee');
    }

    getReferences(): Promise<BusinessReference[]> {
        return notInThisRelease('getReferences');
    }

    saveReference(): Promise<BusinessReference> {
        return notInThisRelease('saveReference');
    }

    deleteReference(): Promise<void> {
        return notInThisRelease('deleteReference');
    }

    getOrgChart(): Promise<OrgChartNode | null> {
        return notInThisRelease('getOrgChart');
    }
}
